<template>
  <v-skeleton-loader
    :loading="loading"
    type="table-thead, table-row-divider@10"
  >
    <v-data-table
      :key="tableKey"
      tile
      class="table-striped custom-pagination"
      :headers="headers"
      :items="inventory"
      :custom-sort="customSort"
      :custom-filter="customFilter"
      :search="search"
      :sort-by="['devicesName']"
      :items-per-page="effectiveItemsPerPage"
      :footer-props="footerProps"
      :loading="loading"
      @update:items-per-page="tableState.itemsPerPage = $event"
    >
      <template #item.devicesName="{ item: { devicesName } }">
        <v-list dense color="transparent">
          <v-list-item
            v-for="(device, index) in devicesName"
            :key="index"
            class="ml-0 pl-0"
          >
            <v-list-item-content>{{ device }}</v-list-item-content>
          </v-list-item>
        </v-list>
      </template>

      <template #item.devicesHealth="{ item: { devices } }">
        <v-list dense color="transparent">
          <v-list-item
            v-for="device in devices"
            :key="device.id"
            class="ml-0 pl-0"
          >
            <v-list-item-content class="pa-0">
              <DeviceGaugeCluster
                :device="device"
                class="device-details flex-grow-0 non-export mr-1"
              />
              <div class="device-details">
                <span
                  v-if="device.endedService"
                  class="hidden-md-and-down d-inline"
                >
                  Ended Service
                </span>
                <v-tooltip bottom v-else-if="device.disposition === 'missing'">
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      v-bind="attrs"
                      v-on="on"
                      class="hidden-md-and-down d-inline"
                    >
                      {{ titleCase(device.disposition) }}
                    </span>
                  </template>
                  <span>
                    Date Reported Missing:
                    <template v-if="device.dateReportedMissing">
                      <span>{{
                        formattedDateReportedMissing(device.dateReportedMissing)
                      }}</span>
                    </template>
                    <template v-else>Unknown</template>
                  </span>
                </v-tooltip>
                <span
                  v-else-if="device.rmaRequest"
                  class="hidden-md-and-down d-inline"
                >
                  {{ rmaRequestText(device.rmaRequest) }}
                </span>
                <span
                  v-else-if="device.disposition != 'in_service'"
                  class="hidden-md-and-down d-inline"
                >
                  {{ titleCase(device.disposition) }}
                </span>
                <span v-else class="hidden-md-and-down d-inline">
                  Lost Contact
                </span>
              </div>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </template>

      <template #item.devicesLastMotion="{ item: { devicesLastMotion } }">
        <v-list dense color="transparent">
          <v-list-item
            v-for="(lastMotion, index) in devicesLastMotion"
            :key="index"
            class="ml-0 pl-0"
          >
            <v-list-item-content>
              {{ lastMotion }}
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </template>

      <template
        #item.devicesLastAssignedTo="{
          item: { devicesLastAssignedTo, devices },
        }"
      >
        <v-list dense color="transparent">
          <v-list-item
            v-for="(lastAssignedTo, index) in devicesLastAssignedTo"
            :key="index"
            class="ml-0 pl-0"
          >
            <v-list-item-icon class="action-btn mr-4">
              <DialogAssignmentHistory :device="devices[index]" />
            </v-list-item-icon>
            <v-list-item-content> {{ lastAssignedTo }} </v-list-item-content>
          </v-list-item>
        </v-list>
      </template>

      <template #item.manage="{ item: { type, devices, name, unitName } }">
        <MenuManageDevice
          v-for="device in devices"
          :key="device.id"
          :device="device"
          :unit-name="unitName || (type === 'unit' ? name : undefined)"
          v-on="$listeners"
        />
      </template>

      <template #footer.prepend>
        <ButtonExportCSV
          class="order-last"
          :table-data="inventory"
          :headers="headers"
          filename="SD_Inventory_Unavailable"
          :trigger-show-all="triggerShowAllForExport"
          :wait-for-data="waitForExportData"
          :after-export="handleAfterExport"
          :get-table-data="getCurrentInventoryData"
        />
        <ButtonPrint
          class="order-last"
          :trigger-show-all="triggerShowAllForPrint"
          :wait-for-data="waitForDataToLoad"
          :after-print="handleAfterPrint"
        />
      </template>
    </v-data-table>
    <div
      v-if="!search && tableState.itemsPerPage > 0"
      class="d-flex justify-center align-center my-4 unavailable-pagination-arrow"
    >
      <div class="mr-2 d-flex align-center">
        <span class="caption">
          {{ paginationInfo }}
        </span>
      </div>
      <v-btn
        class="ml-2"
        icon
        small
        :disabled="!paginationTracking.hasPreviousPage"
        @click="loadPreviousItems"
      >
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>
      <v-btn
        class="ml-2"
        icon
        small
        :disabled="!paginationTracking.hasNextPage"
        @click="loadNextItems"
      >
        <v-icon>mdi-chevron-right</v-icon>
      </v-btn>
    </div>
  </v-skeleton-loader>
</template>

<script>
import DeviceGaugeCluster from "@atoms/DeviceGaugeCluster.vue";
import DialogAssignmentHistory from "./DialogAssignmentHistory.vue";
import MenuManageDevice from "./MenuManageDevice.vue";
import ButtonPrint from "@atoms/ButtonPrint.vue";
import ButtonExportCSV from "@atoms/ButtonExportCSV.vue";

import { titleCase } from "@tod-ui/helpers/strings";
import useDataTable from "@components/common/useDataTable";
import useInventory from "../useInventory";
import { rmaRequestText } from "@tod-ui/helpers/devices";
import { formatDate } from "@tod-ui/helpers/datetime";
import {
  getUnavailableInventoryHeaders,
  commonCustomFilter,
  commonCustomSort,
  createPrintMethods,
  createExportMethods,
  createInventoryWatcher,
  getCommonInventoryData,
  createInventoryExportData,
  COMMON_FOOTER_PROPS,
} from "../helper";

export default {
  name: "InventoryUnavailableTable",
  components: {
    DeviceGaugeCluster,
    DialogAssignmentHistory,
    ButtonPrint,
    ButtonExportCSV,
    MenuManageDevice,
  },
  props: {
    search: {
      type: String,
      default: "",
    },
  },
  setup() {
    const {
      loading: tabLoading,
      endedServiceUnitsLoading,
      unavailableInventoryTab,
      endedServiceInventoryRows,
      unavailablePaginationTracking,
      unavailableItemsPerPage,
      loadNextPage: inventoryLoadNextPage,
      loadPreviousPage: inventoryLoadPreviousPage,
      updateItemsPerPage: inventoryUpdateItemsPerPage,
    } = useInventory();

    const { tableState } = useDataTable({
      itemsPerPage: unavailableItemsPerPage.value,
    });

    return {
      tabLoading,
      endedServiceUnitsLoading,
      inventory: unavailableInventoryTab,
      endedServiceRows: endedServiceInventoryRows,
      paginationTracking: unavailablePaginationTracking,
      tableState,
      loadNextPage: () => inventoryLoadNextPage(2),
      loadPreviousPage: () => inventoryLoadPreviousPage(2),
      inventoryUpdateItemsPerPage,
    };
  },
  data() {
    return {
      ...getCommonInventoryData(),
      ...createInventoryExportData(),
    };
  },
  computed: {
    loading() {
      return this.tabLoading || this.endedServiceUnitsLoading;
    },
    headers() {
      return getUnavailableInventoryHeaders();
    },
    footerProps() {
      return COMMON_FOOTER_PROPS;
    },
    formattedDateReportedMissing() {
      return (date) => {
        if (date) {
          return formatDate(date);
        } else {
          return "Unknown";
        }
      };
    },
    paginationInfo() {
      const { currentPage, pageSize, totalCount } = this.paginationTracking;
      const endedCount = this.endedServiceRows.length;
      const combinedTotal = totalCount + endedCount;
      if (combinedTotal === 0) return "0 items";

      // Ended-service rows only render on page 1 (see unavailableInventoryTab
      // in useInventory.js), so they only shift the range once instead of on
      // every page.
      const startItem =
        currentPage === 1 ? 1 : endedCount + (currentPage - 1) * pageSize + 1;
      const endItem = endedCount + Math.min(currentPage * pageSize, totalCount);

      return `${startItem}-${endItem} of ${combinedTotal}`;
    },
    effectiveItemsPerPage() {
      const base = this.tableState.itemsPerPage;
      if (base <= 0 || this.paginationTracking.currentPage !== 1) return base;
      return base + this.endedServiceRows.length;
    },
  },
  watch: {
    "tableState.itemsPerPage"(newValue) {
      if (!this.search) {
        this.inventoryUpdateItemsPerPage(
          2,
          newValue,
          this.$apollo.provider.defaultClient
        );
      }
    },
    search: {
      handler(newValue, oldValue) {
        if (oldValue && !newValue) {
          this.inventoryUpdateItemsPerPage(
            2,
            this.tableState.itemsPerPage,
            this.$apollo.provider.defaultClient
          );
        }
      },
      immediate: false,
    },

    ...createInventoryWatcher(),
  },
  methods: {
    customFilter: commonCustomFilter,
    customSort: commonCustomSort,

    ...createPrintMethods(2, function (newValue) {
      this.inventoryUpdateItemsPerPage(
        2,
        newValue,
        this.$apollo.provider.defaultClient
      );
    }),

    ...createExportMethods(2),

    titleCase,
    rmaRequestText,

    loadNextItems() {
      this.loadNextPage();
    },

    loadPreviousItems() {
      this.loadPreviousPage();
    },
  },
};
</script>

<style>
.device-details {
  flex: 1 1 auto;
}
.custom-pagination .v-data-footer__select {
  margin-right: 100px;
}
.custom-pagination .v-data-footer__icons-before,
.custom-pagination .v-data-footer__icons-after {
  visibility: hidden !important;
}
.custom-pagination .v-data-footer__pagination {
  display: none !important;
}
.unavailable-pagination-arrow {
  position: absolute;
  bottom: -5px;
  right: 70px;
  z-index: 1;
}
</style>
